<template>
  <div class="main-content">
    <!-- Breadcrumb / header -->
    <div
      v-if="node.id"
      class="row mb-2"
    >
      <div class="col-md-12">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="GitBranchIcon"
            size="22"
            class="mr-1 text-primary"
          />
          <div>
            <h4 class="mb-0">
              {{ node.name }}
            </h4>
            <small class="text-muted">
              {{ node.type ? node.type.name : "" }}
              <span v-if="node.parent">
                &nbsp;·&nbsp;
                <feather-icon
                  icon="CornerDownRightIcon"
                  size="12"
                />
                {{ node.parent.name }}
              </span>
            </small>
          </div>
          <div class="ml-auto">
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="$router.push('/nodes')"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="14"
                class="mr-50"
              />
              {{ $t("common.back") }}
            </b-button>
          </div>
        </div>
        <hr>
      </div>
    </div>

    <b-tabs
      v-if="node.id"
      content-class="mt-2"
      @input="onTabChange"
    >
      <!-- ─── ABA DETALHES ─────────────────────────────────────────────── -->
      <b-tab
        :title="$t('node.admin.detailsTab')"
        active
      >
        <div class="card">
          <div class="card-body">
            <h6 class="node-form-section">{{ $t("node.form.sectionIdentification") }}</h6>
            <div class="form-row">
              <div class="form-group col-md-6 col-xl-3">
                <label><span class="required">*</span>
                  {{ $t("node.form.nameLabel") }}</label>
                <input
                  v-model="node.name"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-6 col-xl-3">
                <label><span class="required">*</span>
                  {{ $t("node.form.typeLabel") }}</label>
                <v-select
                  v-model="node.type_id"
                  :options="nodeTypes"
                  :reduce="(t) => t.id"
                  label="name"
                  class="vselect"
                  :placeholder="$t('node.form.typePlaceholder')"
                />
              </div>
              <div class="form-group col-md-6 col-xl-3">
                <label>{{ $t("node.form.parentLabel") }}
                  <small class="text-muted">{{
                    $t("node.admin.parentHint")
                  }}</small>:</label>
                <v-select
                  v-model="node.parent_id"
                  :options="allNodes.filter((n) => n.id !== node.id)"
                  :reduce="(n) => n.id"
                  label="name"
                  class="vselect"
                  :placeholder="$t('node.form.parentPlaceholder')"
                  :clearable="true"
                />
              </div>
              <div class="form-group col-md-6 col-xl-3">
                <label>{{ $t("node.form.tenantIdLabel") }}
                  <small class="text-muted">{{
                    $t("node.form.optionalHint")
                  }}</small>:</label>
                <input
                  v-model="node.tenant_id"
                  type="number"
                  class="form-control"
                >
              </div>
            </div>

            <h6 class="node-form-section">{{ $t("node.form.sectionVisibility") }}</h6>
            <div class="form-row">
              <div class="form-group col-md-6">
                <b-form-checkbox
                  v-model="node.share_parent_info"
                  switch
                >
                  {{ $t("node.form.shareParentInfoLabel") }}
                </b-form-checkbox>
                <small class="text-muted">{{ $t("node.form.shareParentInfoHint") }}</small>
              </div>
              <div class="form-group col-md-6">
                <b-form-checkbox
                  v-model="node.only_registered_coverage"
                  switch
                >
                  {{ $t("node.form.onlyRegisteredCoverageLabel") }}
                </b-form-checkbox>
                <small class="text-muted">{{ $t("node.form.onlyRegisteredCoverageHint") }}</small>
              </div>
            </div>

            <h6 class="node-form-section">{{ $t("node.form.sectionOperation") }}</h6>
            <div class="form-row">
              <div class="form-group col-md-4">
                <label for="nodeAddressSearchMode">{{ $t("node.form.addressSearchModeLabel") }}</label>
                <b-form-select
                  id="nodeAddressSearchMode"
                  v-model="node.address_search_mode"
                  :options="addressSearchModeOptions"
                />
                <small class="text-muted">{{ $t("node.form.addressSearchModeHint") }}</small>
              </div>
              <div class="form-group col-md-4">
                <label for="nodeDefaultDdi">{{ $t("node.form.defaultDdiLabel") }}</label>
                <ddi-select
                  id="nodeDefaultDdi"
                  v-model="node.default_ddi"
                />
                <small class="text-muted">{{ $t("node.form.defaultDdiHint") }}</small>
              </div>
              <div class="form-group col-md-4">
                <label for="nodeIdleAfter">{{ $t("node.form.idleAfterLabel") }}</label>
                <input
                  id="nodeIdleAfter"
                  v-model.number="node.idle_after_minutes"
                  type="number"
                  min="1"
                  max="240"
                  class="form-control"
                >
                <small class="text-muted">{{ $t("node.form.idleAfterHint") }}</small>
              </div>
            </div>
            <h6 class="node-form-section">{{ $t("node.form.sectionDocuments") }}</h6>
            <div class="form-row">
              <div class="form-group col-12">
                <label>{{ $t("node.form.logoLabel") }}</label>
                <div class="node-logo">
                  <!-- Topo de uma folha A4: a logo na largura real do PDF (9 cm de 21 cm) -->
                  <div class="node-logo__sheet">
                    <img
                      v-if="logoPreviewUrl"
                      class="node-logo__img"
                      :src="logoPreviewUrl"
                      :alt="node.name"
                    >
                    <span
                      v-else
                      class="node-logo__empty"
                    >{{ $t("node.form.logoEmpty") }}</span>
                    <span class="node-logo__line node-logo__line--title" />
                    <span class="node-logo__line" />
                    <span class="node-logo__line node-logo__line--short" />
                  </div>
                  <div class="node-logo__actions">
                    <template v-if="logoPending">
                      <span class="node-logo__pending">{{ $t("node.form.logoPending") }}</span>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm"
                        @click="confirmLogo"
                      >
                        {{ $t("node.form.logoUse") }}
                      </button>
                      <a
                        href="#"
                        class="small"
                        @click.prevent="cancelLogo"
                      >{{ $t("node.form.logoCancel") }}</a>
                    </template>
                    <template v-else>
                      <label class="btn btn-outline-primary btn-sm mb-0">
                        <i class="feather icon-upload" />
                        {{ node.logo_url ? $t("node.form.logoChange") : $t("node.form.logoUpload") }}
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          hidden
                          @change="pickLogo"
                        >
                      </label>
                      <a
                        v-if="node.logo_url"
                        href="#"
                        class="text-danger small"
                        @click.prevent="removeLogo"
                      >{{ $t("node.form.logoRemove") }}</a>
                    </template>
                  </div>
                </div>
                <small class="text-muted">{{ $t("node.form.logoHint") }}</small>
              </div>
            </div>
            <!-- Campos dinâmicos do tipo selecionado -->
            <div
              v-if="fieldSchema.length"
              class="mt-1"
            >
              <hr>
              <p class="font-weight-bold mb-2">
                {{ $t("node.form.typeInfoTitle") }}
                <em>{{ node.type ? node.type.name : "" }}</em>
              </p>
              <div class="form-row">
                <div
                  v-for="field in fieldSchema"
                  :key="field.key"
                  :class="['form-group', field.col || 'col-md-6']"
                >
                  <label>
                    <span
                      v-if="field.required"
                      class="required"
                    >*</span>
                    {{ field.label }}:
                  </label>
                  <input
                    v-if="field.type === 'text'"
                    v-model="node.metadata[field.key]"
                    type="text"
                    class="form-control"
                  >
                  <input
                    v-else-if="field.type === 'number'"
                    v-model="node.metadata[field.key]"
                    type="number"
                    class="form-control"
                  >
                  <textarea
                    v-else-if="field.type === 'textarea'"
                    v-model="node.metadata[field.key]"
                    class="form-control"
                    rows="3"
                  />
                  <select
                    v-else-if="field.type === 'select'"
                    v-model="node.metadata[field.key]"
                    class="form-control"
                  >
                    <option value="">
                      {{ $t("node.form.selectOption") }}
                    </option>
                    <option
                      v-for="opt in field.options"
                      :key="opt"
                      :value="opt"
                    >
                      {{ opt }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <b-button
              v-if="$hasAbility('node.admin')"
              variant="primary"
              @click="saveNode"
            >{{ $t("node.admin.saveDetailsButton") }}</b-button>
          </div>
        </div>
      </b-tab>

      <!-- ─── ABA SUB-NÓS ───────────────────────────────────────────────── -->
      <b-tab
        :title="$t('node.admin.subNodesTabTitle', { count: children.length })"
      >
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <span
              class="card-title mb-0"
            >{{ $t("node.admin.childrenOfLabel") }}
              <strong>{{ node.name }}</strong></span>
            <b-button
              v-if="$hasAbility('node.admin')"
              size="sm"
              variant="primary"
              @click="openAddChild"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-50"
              />
              {{ $t("node.admin.addSubNodeButton") }}
            </b-button>
          </div>
          <div class="card-body p-0">
            <table
              v-if="children.length"
              class="table table-hover mb-0"
            >
              <thead>
                <tr>
                  <th>{{ $t("node.admin.colId") }}</th>
                  <th>{{ $t("node.admin.colName") }}</th>
                  <th>{{ $t("node.admin.colType") }}</th>
                  <th>{{ $t("node.admin.colUsers") }}</th>
                  <th>{{ $t("common.actions") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="child in children"
                  :key="child.id"
                >
                  <td>{{ child.id }}</td>
                  <td>{{ child.name }}</td>
                  <td>{{ child.type ? child.type.name : "-" }}</td>
                  <td>{{ child.node_users_count || 0 }}</td>
                  <td>
                    <b-button
                      v-if="$hasAbility('node.ver')"
                      size="sm"
                      variant="outline-primary"
                      class="mr-1"
                      :title="$t('node.actions.administer')"
                      @click="goToAdmin(child.id)"
                    >
                      <feather-icon
                        icon="SettingsIcon"
                        size="14"
                      />
                    </b-button>
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      size="sm"
                      variant="outline-danger"
                      :title="$t('node.actions.removeFromParent')"
                      @click="unlinkChild(child)"
                    >
                      <feather-icon
                        icon="XIcon"
                        size="14"
                      />
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              v-else
              class="p-3 text-center text-muted"
            >
              {{ $t("node.admin.noSubNodes") }}
            </div>
          </div>
        </div>

        <!-- Modal sub-nó -->
        <b-modal
          id="modal-add-child"
          :title="$t('node.admin.addSubNodeButton')"
          :ok-title="$t('common.save')"
          :cancel-title="$t('common.cancel')"
          @ok="saveChild"
        >
          <div class="form-group">
            <label>{{ $t("node.admin.linkExistingNodeLabel") }}</label>
            <v-select
              v-model="newChildId"
              :options="availableChildren"
              :reduce="(n) => n.id"
              label="name"
              class="vselect"
              :placeholder="$t('node.admin.selectChildNodePlaceholder')"
            />
          </div>
          <hr>
          <p class="text-muted small mb-1">
            {{ $t("node.admin.orCreateNewChildLabel") }}
          </p>
          <b-button
            v-if="$hasAbility('node.admin')"
            size="sm"
            variant="outline-primary"
            @click="$router.push('/nodes/add?parent_id=' + node.id)"
          >
            <feather-icon
              icon="PlusIcon"
              size="13"
              class="mr-50"
            />
            {{ $t("node.admin.createNewChildNodeButton") }}
          </b-button>
        </b-modal>
      </b-tab>

      <!-- ─── ABA EMPRESA ──────────────────────────────────────────────── -->
      <b-tab
        :title="
          company.id
            ? $t('node.admin.companyTabLinked')
            : $t('node.admin.companyTab')
        "
      >
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <span class="card-title mb-0">
              {{
                company.id
                  ? $t("node.admin.companyLinkedToLabel")
                  : $t("node.admin.companyRegisterForLabel")
              }}<strong>{{ node.name }}</strong>
            </span>
            <b-button
              v-if="company.id && $hasAbility('node.admin')"
              size="sm"
              variant="outline-danger"
              @click="unlinkCompany"
            >
              <feather-icon
                icon="XIcon"
                size="14"
                class="mr-50"
              />
              {{ $t("node.actions.unlink") }}
            </b-button>
          </div>
          <div class="card-body">
            <div class="form-row">
              <div class="form-group col-md-5">
                <label><span class="required">*</span>
                  {{ $t("node.form.nameLabel") }}</label>
                <input
                  v-model="company.name"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-4">
                <label>{{ $t("node.admin.fantasyNameLabel") }}</label>
                <input
                  v-model="company.fantasy_name"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-3">
                <label><span class="required">*</span>
                  {{ $t("node.admin.cnpjLabel") }}</label>
                <input
                  v-model="company.cnpj"
                  v-mask="'99.999.999/9999-99'"
                  type="text"
                  class="form-control"
                >
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-4">
                <label><span class="required">*</span>
                  {{ $t("node.admin.companyTypeLabel") }}</label>
                <input
                  v-model="company.type"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-3">
                <label>{{ $t("node.admin.phoneLabel") }}</label>
                <input
                  v-model="company.phone"
                  v-mask="'(99) 99999-9999'"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-2">
                <label>{{ $t("node.admin.cepLabel") }}</label>
                <input
                  v-model="company.cep"
                  v-mask="'99999-999'"
                  type="text"
                  class="form-control"
                  @keyup="buscarCep"
                >
              </div>
              <div class="form-group col-md-3">
                <label>{{ $t("common.status") }}:</label>
                <select
                  v-model="company.status"
                  class="form-control"
                >
                  <option :value="1">
                    {{ $t("common.active") }}
                  </option>
                  <option :value="0">
                    {{ $t("common.inactive") }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-4">
                <label>{{ $t("node.admin.addressLabel") }}</label>
                <input
                  v-model="company.address"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-3">
                <label>{{ $t("node.admin.neighborhoodLabel") }}</label>
                <input
                  v-model="company.neighborhood"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-1">
                <label>{{ $t("node.admin.numberLabel") }}</label>
                <input
                  id="inputNumeroEmpresa"
                  v-model="company.number"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-4">
                <label>{{ $t("node.admin.complementLabel") }}</label>
                <input
                  v-model="company.complement"
                  type="text"
                  class="form-control"
                >
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-3">
                <label>{{ $t("node.admin.ufLabel") }}</label>
                <v-select
                  v-model="company.state_id"
                  :options="ufs"
                  :reduce="(u) => u.id"
                  label="name"
                  class="vselect"
                  @input="getCities"
                />
              </div>
              <div class="form-group col-md-5">
                <label>{{ $t("node.admin.cityLabel") }}</label>
                <v-select
                  v-model="company.city_id"
                  :options="cities"
                  :reduce="(c) => c.id"
                  label="name"
                  class="vselect"
                />
              </div>
              <div class="form-group col-md-4">
                <label>{{ $t("node.admin.referenceLabel") }}</label>
                <input
                  v-model="company.reference"
                  type="text"
                  class="form-control"
                >
              </div>
            </div>
            <b-button
              v-if="$hasAbility('node.admin')"
              variant="primary"
              @click="saveCompany"
            >
              <feather-icon
                icon="SaveIcon"
                size="14"
                class="mr-50"
              />
              {{
                company.id
                  ? $t("node.admin.saveCompanyButton")
                  : $t("node.admin.registerCompanyButton")
              }}
            </b-button>
          </div>
        </div>
      </b-tab>

      <!-- ─── ABA ABRANGÊNCIA ────────────────────────────────────────────── -->
      <b-tab :title="`Abrangência (${coverages.length})`">
        <div class="row">
          <!-- Formulário de adicionar cobertura -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">{{
                  $t("node.admin.addCoverageTitle")
                }}</span>
              </div>
              <div class="card-body">
                <!-- Banner de restrição herdada do nó pai -->
                <div
                  v-if="parentConstraint"
                  class="alert alert-info py-1 px-2 small mb-2 d-flex align-items-start"
                >
                  <feather-icon
                    icon="InfoIcon"
                    size="14"
                    class="mr-1 mt-25 flex-shrink-0"
                  />
                  <span>{{ parentConstraint.message }}</span>
                </div>

                <div class="form-group">
                  <label>{{ $t("node.form.typeLabel") }}</label>
                  <v-select
                    v-model="newCoverage.coverage_type"
                    :options="coverageTypeOptions"
                    :reduce="(o) => o.value"
                    label="label"
                    class="vselect"
                    :placeholder="
                      $t('node.admin.selectCoverageTypePlaceholder')
                    "
                    @input="onCoverageTypeChange"
                  />
                </div>

                <!-- Estado -->
                <div
                  v-if="newCoverage.coverage_type === 'state'"
                  class="form-group"
                >
                  <label>{{ $t("node.admin.stateLabel") }}</label>
                  <v-select
                    v-model="newCoverage.uf_id"
                    :options="ufsForCoverage"
                    :reduce="(u) => u.id"
                    label="name"
                    class="vselect"
                    :placeholder="$t('node.admin.selectStatePlaceholder')"
                  />
                </div>

                <!-- Município -->
                <template v-if="newCoverage.coverage_type === 'city'">
                  <div class="form-group">
                    <label>{{ $t("node.admin.filterByUfLabel") }}</label>
                    <v-select
                      v-model="coverageUfFilter"
                      :options="ufsForCoverageFiltered"
                      :reduce="(u) => u.id"
                      label="name"
                      class="vselect"
                      :placeholder="$t('node.admin.selectUfPlaceholder')"
                      :disabled="
                        parentConstraint &&
                          parentConstraint.lockedUfIds.length === 1
                      "
                      @input="loadCitiesForCoverage"
                    />
                  </div>
                  <div class="form-group">
                    <label>{{ $t("node.admin.municipalityLabel") }}</label>
                    <v-select
                      v-model="newCoverage.city_id"
                      :options="citiesForCoverage"
                      :reduce="(c) => c.id"
                      label="name"
                      class="vselect"
                      :placeholder="
                        $t('node.admin.selectMunicipalityPlaceholder')
                      "
                      :disabled="!coverageUfFilter"
                    />
                  </div>
                </template>

                <!-- Bairro -->
                <template v-if="newCoverage.coverage_type === 'neighborhood'">
                  <div
                    v-if="parentConstraint && parentConstraint.neighborhoodHint"
                    class="alert alert-warning py-1 px-2 small mb-1"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      size="12"
                      class="mr-50"
                    />
                    {{ parentConstraint.neighborhoodHint }}
                  </div>
                  <div class="form-group">
                    <label>{{
                      $t("node.admin.searchNeighborhoodLabel")
                    }}</label>
                    <gmap-autocomplete
                      class="form-control"
                      :placeholder="
                        $t('node.admin.neighborhoodPlaceholderExample')
                      "
                      :options="neighborhoodAutocompleteOptions"
                      @place_changed="onNeighborhoodPlaceSelected"
                    />
                    <small class="text-muted">
                      {{ $t("node.admin.neighborhoodSearchHint") }}
                    </small>
                  </div>
                  <div
                    v-if="newCoverage.neighborhood_name"
                    class="alert alert-success py-1 px-2 small"
                  >
                    <strong>{{ $t("node.admin.selectedLabel") }}</strong>
                    {{ newCoverage.neighborhood_name }}
                  </div>
                </template>

                <b-button
                  v-if="$hasAbility('node.admin')"
                  variant="primary"
                  :disabled="!canAddCoverage"
                  @click="addCoverage"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="14"
                    class="mr-50"
                  />
                  {{ $t("common.add") }}
                </b-button>
              </div>
            </div>

            <!-- Lista de coberturas adicionadas -->
            <div
              v-if="coverages.length"
              class="card mt-1"
            >
              <div class="card-header">
                <span class="card-title mb-0">{{
                  $t("node.admin.coveragesDefinedTitle")
                }}</span>
              </div>
              <ul class="list-group list-group-flush">
                <li
                  v-for="cov in coverages"
                  :key="cov.id"
                  class="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span>
                    <b-badge
                      :variant="coverageBadge(cov.coverage_type)"
                      class="mr-50"
                    >
                      {{ coverageLabel(cov.coverage_type) }}
                    </b-badge>
                    {{ coverageDescription(cov) }}
                  </span>
                  <b-button
                    v-if="$hasAbility('node.admin')"
                    size="sm"
                    variant="outline-danger"
                    @click="removeCoverage(cov)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="12"
                    />
                  </b-button>
                </li>
              </ul>
            </div>
          </div>

          <!-- Mapa -->
          <div class="col-md-8">
            <div class="card">
              <div
                class="card-body p-0"
                style="height: 500px; position: relative"
              >
                <div
                  v-if="coveragesLoading"
                  class="d-flex align-items-center justify-content-center"
                  style="height: 100%"
                >
                  <b-spinner variant="primary" />
                </div>
                <gmap-map
                  v-else
                  ref="coverageMap"
                  :center="mapCenter"
                  :zoom="mapZoom"
                  style="width: 100%; height: 100%"
                  :options="{ streetViewControl: false, mapTypeControl: false }"
                >
                  <gmap-polygon
                    v-for="(poly, idx) in mapPolygons"
                    :key="idx"
                    :paths="poly.paths"
                    :options="{
                      fillColor: poly.color,
                      fillOpacity: 0.25,
                      strokeColor: poly.color,
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                    }"
                  />
                </gmap-map>
              </div>
            </div>
          </div>
        </div>

        <!-- Botão ver árvore de sub-nós -->
        <div
          v-if="$hasAbility('node.admin')"
          class="text-right mt-1"
        >
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="openTreeMap"
          >
            <feather-icon
              icon="MapIcon"
              size="14"
              class="mr-50"
            />
            {{ $t("node.admin.viewFullMapButton") }}
          </b-button>
        </div>
      </b-tab>

      <!-- Disciplinas -->
      <b-tab
        :title="
          $t('node.admin.disciplinesTabTitle', {
            count: selectedDisciplineIds.length,
          })
        "
      >
        <div class="card">
          <div
            class="card-header d-flex justify-content-between align-items-center"
          >
            <span
              class="card-title mb-0"
            >{{ $t("node.admin.disciplinesServedByLabel") }}
              <strong>{{ node.name }}</strong></span>
            <small class="text-muted">
              {{
                $t("node.admin.activeCountLabel", {
                  selected: selectedDisciplineIds.length,
                  total: allDisciplines.length,
                })
              }}
            </small>
          </div>
          <div class="card-body p-0">
            <p
              v-if="!allDisciplines.length"
              class="text-muted p-3 mb-0"
            >
              {{ $t("node.admin.noDisciplinesRegistered") }}
            </p>
            <table
              v-else
              class="table table-hover mb-0"
            >
              <thead>
                <tr>
                  <th style="width: 56px">
                    {{ $t("common.active") }}
                  </th>
                  <th>{{ $t("node.admin.colDiscipline") }}</th>
                  <th style="width: 120px">{{ $t("node.admin.colPrefix") }}</th>
                  <th>{{ $t("node.admin.colDescription") }}</th>
                  <th style="width: 60px" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="disc in allDisciplines"
                  :key="disc.id"
                >
                  <td class="align-middle">
                    <b-form-checkbox
                      :checked="selectedDisciplineIds.includes(disc.id)"
                      switch
                      @change="toggleDiscipline(disc.id)"
                    />
                  </td>
                  <td class="align-middle">
                    <span
                      :class="
                        selectedDisciplineIds.includes(disc.id)
                          ? 'font-weight-bold text-primary'
                          : 'text-muted'
                      "
                    >{{ disc.name }}</span>
                  </td>
                  <td class="align-middle">
                    <b-badge
                      v-if="disc.prefix"
                      variant="light-primary"
                      class="font-weight-bold"
                      style="font-family: monospace; letter-spacing: 0.04em"
                    >{{ disc.prefix }}</b-badge>
                    <small
                      v-else
                      class="text-muted"
                    >—</small>
                  </td>
                  <td class="align-middle">
                    <small class="text-muted">{{
                      disc.description || "—"
                    }}</small>
                  </td>
                  <td class="align-middle text-center">
                    <b-button
                      v-if="$hasAbility('node.ver')"
                      size="sm"
                      variant="outline-secondary"
                      :title="$t('node.actions.configureDiscipline')"
                      @click="openDisciplineConfig(disc)"
                    >
                      <feather-icon
                        icon="SettingsIcon"
                        size="14"
                      />
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            class="card-footer d-flex align-items-center justify-content-between"
          >
            <b-button
              v-if="$hasAbility('node.admin')"
              variant="primary"
              size="sm"
              @click="saveDisciplines"
            >
              <feather-icon
                icon="SaveIcon"
                size="14"
                class="mr-50"
              />
              {{ $t("common.save") }}
            </b-button>
            <b-button
              v-if="selectedDisciplineIds.length"
              variant="outline-secondary"
              size="sm"
              @click="selectedDisciplineIds = []"
            >
              {{ $t("node.admin.clearSelectionButton") }}
            </b-button>
          </div>
        </div>

        <!-- Modal configurar disciplina removido — usa tela dedicada /nodes/admin/:id/discipline/:disciplineId -->
      </b-tab>

      <!-- ─── ABA PROFISSIONAIS ─────────────────────────────────────────── -->
      <b-tab
        :title="
          $t('node.admin.professionalsTabTitle', {
            count: nodeProfessionalsPagination.count,
          })
        "
      >
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <span
              class="card-title mb-0"
            >{{ $t("node.admin.professionalsLinkedToLabel") }}
              <strong>{{ node.name }}</strong></span>
            <b-button
              v-if="$hasAbility('node.admin')"
              size="sm"
              variant="primary"
              @click="openAddProfessional"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-50"
              />
              {{ $t("node.admin.linkProfessionalButton") }}
            </b-button>
          </div>
          <div class="card-body border-bottom">
            <div class="form-row">
              <div class="form-group col-md-4 mb-0">
                <input
                  v-model="nodeProfessionalsFilters.professionalName"
                  type="text"
                  class="form-control"
                  :placeholder="
                    $t('node.admin.filterProfessionalNamePlaceholder')
                  "
                  @input="onNodeProfessionalsFilterChange"
                >
              </div>
              <div class="form-group col-md-4 mb-0">
                <v-select
                  v-model="nodeProfessionalsFilters.professionalTypeId"
                  :options="allProfessionalTypes"
                  :reduce="(t) => t.id"
                  label="name"
                  class="vselect"
                  :placeholder="
                    $t('node.admin.filterProfessionalTypePlaceholder')
                  "
                  @input="onNodeProfessionalsFilterChange"
                />
              </div>
              <div class="form-group col-md-4 mb-0">
                <v-select
                  v-model="nodeProfessionalsFilters.roleId"
                  :options="allRoles"
                  :reduce="(r) => r.id"
                  label="name"
                  class="vselect"
                  :placeholder="$t('node.admin.filterRolePlaceholder')"
                  @input="onNodeProfessionalsFilterChange"
                />
              </div>
            </div>
          </div>
          <div class="card-body p-0">
            <table
              v-if="nodeProfessionals.length"
              class="table table-hover mb-0"
            >
              <thead>
                <tr>
                  <th
                    class="sortable"
                    @click="toggleNodeProfessionalsSort('name')"
                  >
                    {{ $t("node.admin.colProfessional") }}
                    <feather-icon
                      v-if="nodeProfessionalsSort.by === 'name'"
                      :icon="nodeProfessionalsSort.dir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                      size="12"
                    />
                  </th>
                  <th
                    class="sortable"
                    @click="toggleNodeProfessionalsSort('professional_type')"
                  >
                    {{ $t("node.admin.colProfessionalType") }}
                    <feather-icon
                      v-if="nodeProfessionalsSort.by === 'professional_type'"
                      :icon="nodeProfessionalsSort.dir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                      size="12"
                    />
                  </th>
                  <th
                    class="sortable"
                    @click="toggleNodeProfessionalsSort('role')"
                  >
                    {{ $t("node.admin.colRole") }}
                    <feather-icon
                      v-if="nodeProfessionalsSort.by === 'role'"
                      :icon="nodeProfessionalsSort.dir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                      size="12"
                    />
                  </th>
                  <th
                    class="sortable"
                    @click="toggleNodeProfessionalsSort('starts_at')"
                  >
                    {{ $t("node.admin.colValidity") }}
                    <feather-icon
                      v-if="nodeProfessionalsSort.by === 'starts_at'"
                      :icon="nodeProfessionalsSort.dir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                      size="12"
                    />
                  </th>
                  <th>{{ $t("common.actions") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="np in nodeProfessionals"
                  :key="np.id"
                >
                  <td>
                    {{
                      np.professional
                        ? np.professional.name
                        : np.professional_id
                    }}
                  </td>
                  <td>
                    {{ np.professional_type ? np.professional_type.name : "-" }}
                  </td>
                  <td>{{ np.role ? np.role.name : "-" }}</td>
                  <td>
                    <span class="text-nowrap">
                      {{ formatIsoDate(np.starts_at) }} →
                      {{
                        np.ends_at
                          ? formatIsoDate(np.ends_at)
                          : $t("node.admin.noEndDate")
                      }}
                    </span>
                    <b-badge
                      :variant="linkStatus(np).variant"
                      class="ml-50"
                    >
                      {{ linkStatus(np).label }}
                    </b-badge>
                  </td>
                  <td>
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      size="sm"
                      variant="outline-primary"
                      class="mr-50"
                      :title="$t('node.actions.editValidity')"
                      @click="openEditLink(np)"
                    >
                      <feather-icon
                        icon="CalendarIcon"
                        size="14"
                      />
                    </b-button>
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      size="sm"
                      variant="outline-danger"
                      :title="$t('node.actions.unlink')"
                      @click="unlinkProfessional(np)"
                    >
                      <feather-icon
                        icon="XIcon"
                        size="14"
                      />
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              v-else
              class="p-3 text-center text-muted"
            >
              {{ $t("node.admin.noProfessionalsLinked") }}
            </div>
          </div>
          <div
            v-if="nodeProfessionalsPagination.pages > 1"
            class="card-footer d-flex justify-content-center"
          >
            <pagination
              v-model="nodeProfessionalsPagination.current_page"
              :options="opcoesPaginacao"
              :records="nodeProfessionalsPagination.count"
              :per-page="nodeProfessionalsPagination.per_page"
              @paginate="changeNodeProfessionalsPage"
            />
          </div>
        </div>

        <!-- Modal profissional -->
        <b-modal
          id="modal-add-professional"
          :title="$t('node.admin.linkProfessionalModalTitle')"
          :ok-title="$t('node.admin.linkOkTitle')"
          :cancel-title="$t('common.cancel')"
          @ok="saveProfessional"
        >
          <div class="form-group">
            <label>{{ $t("node.admin.professionalLabel") }}</label>
            <v-select
              v-model="newProfessional.professional_id"
              :options="availableProfessionals"
              :reduce="(p) => p.id"
              label="name"
              class="vselect"
              :placeholder="$t('node.admin.selectProfessionalPlaceholder')"
            />
          </div>
          <div class="form-group">
            <label>{{ $t("node.admin.professionalTypeLabel") }}</label>
            <v-select
              v-model="newProfessional.professional_type_id"
              :options="allProfessionalTypes"
              :reduce="(t) => t.id"
              label="name"
              class="vselect"
              :placeholder="$t('node.admin.selectProfessionalTypePlaceholder')"
            />
          </div>
          <div class="form-group">
            <label>{{ $t("node.admin.roleLabel") }}
              <small class="text-muted">{{ $t("node.admin.roleHint") }}</small>:</label>
            <v-select
              v-model="newProfessional.role_id"
              :options="allRoles"
              :reduce="(r) => r.id"
              label="name"
              class="vselect"
              :placeholder="$t('node.admin.selectRolePlaceholder')"
            />
          </div>
          <div class="form-row">
            <div class="form-group col-6">
              <label><span class="required">*</span>
                {{ $t("node.admin.startDateLabel") }}</label>
              <input
                v-model="newProfessional.starts_at"
                type="date"
                class="form-control"
              >
            </div>
            <div class="form-group col-6">
              <label>{{ $t("node.admin.endDateLabel") }}
                <small class="text-muted">{{
                  $t("node.form.optionalHint")
                }}</small>:</label>
              <input
                v-model="newProfessional.ends_at"
                type="date"
                class="form-control"
                :min="newProfessional.starts_at"
              >
            </div>
          </div>
        </b-modal>

        <b-modal
          id="modal-edit-link"
          :title="$t('node.admin.editLinkModalTitle')"
          :ok-title="$t('common.save')"
          :cancel-title="$t('common.cancel')"
          @ok="saveEditLink"
        >
          <div class="form-row">
            <div class="form-group col-6">
              <label><span class="required">*</span>
                {{ $t("node.admin.startDateLabel") }}</label>
              <input
                v-model="editingLink.starts_at"
                type="date"
                class="form-control"
              >
            </div>
            <div class="form-group col-6">
              <label>{{ $t("node.admin.endDateLabel") }}
                <small class="text-muted">{{
                  $t("node.form.optionalHint")
                }}</small>:</label>
              <input
                v-model="editingLink.ends_at"
                type="date"
                class="form-control"
                :min="editingLink.starts_at"
              >
            </div>
          </div>
        </b-modal>
      </b-tab>

      <!-- ─── ABA EQUIPES ──────────────────────────────────────────────── -->
      <!-- Equipes e tipos de equipe são cadastro da central (node_id).
           ?tab=teams abre direto aqui — é para onde o form da equipe volta. -->
      <b-tab
        lazy
        :active="$route.query.tab === 'teams'"
        :title="$t('node.admin.teamsTabTitle')"
      >
        <div class="row">
          <div class="col-xl-8">
            <team-list
              ref="teamList"
              :node-id="node.id"
              :has-children="children.length > 0"
            />
          </div>
          <div class="col-xl-4">
            <team-type-list
              :node-id="node.id"
              :has-children="children.length > 0"
              @changed="$refs.teamList && $refs.teamList.carregar()"
            />
          </div>
        </div>
      </b-tab>

      <!-- ─── ABA EQUIPAMENTOS ──────────────────────────────────────────── -->
      <b-tab :title="$t('node.admin.equipmentTabTitle')">
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <span class="card-title mb-0">{{
              $t("node.admin.equipmentOfNodeTitle")
            }}</span>
            <router-link
              :to="{
                name: 'list-equipment-types',
                query: { node_id: node.id },
              }"
              class="btn btn-sm btn-outline-secondary"
              :title="$t('node.admin.equipmentTypesSharedHint')"
            >
              {{ $t("node.admin.equipmentTypesLinkLabel") }}
            </router-link>
          </div>
          <div class="card-body">
            <equipment-list
              v-if="node && node.id"
              :node-id="node.id"
            />
          </div>
        </div>
      </b-tab>

      <!-- ── Aba: Integração de Materiais (DIM) ── -->
      <b-tab :title="$t('node.admin.materialsTabTitle')">
        <div class="row">
          <div class="col-md-7">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">{{
                  $t("node.admin.dimIntegrationTitle")
                }}</span>
              </div>
              <div class="card-body">
                <div
                  v-if="dimLoading"
                  class="text-center py-2"
                >
                  <b-spinner small /> {{ $t("node.admin.loadingLabel") }}
                </div>
                <template v-else>
                  <div class="form-group">
                    <label>{{ $t("node.admin.dimBaseUrlLabel") }}</label>
                    <input
                      v-model="dim.base_url"
                      class="form-control"
                      type="text"
                      placeholder="https://dim.exemplo.com/api/v1"
                    >
                  </div>
                  <div class="form-group">
                    <label>{{ $t("node.admin.dimUsernameLabel") }}</label>
                    <input
                      v-model="dim.username"
                      class="form-control"
                      type="text"
                      placeholder="usuario@exemplo.com"
                    >
                  </div>
                  <div class="form-group">
                    <label>{{
                      dim.has_password
                        ? $t("node.admin.dimPasswordKeepLabel")
                        : $t("node.admin.dimPasswordLabel")
                    }}</label>
                    <input
                      v-model="dim.password"
                      class="form-control"
                      type="password"
                      autocomplete="new-password"
                      placeholder="••••••••"
                    >
                  </div>
                  <div class="custom-control custom-switch mb-1">
                    <input
                      id="dim-enabled"
                      v-model="dim.enabled"
                      type="checkbox"
                      class="custom-control-input"
                    >
                    <label
                      class="custom-control-label"
                      for="dim-enabled"
                    >{{
                      $t("node.admin.dimEnabledLabel")
                    }}</label>
                  </div>
                  <div
                    class="d-flex flex-wrap"
                    style="gap: 8px"
                  >
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      variant="outline-secondary"
                      size="sm"
                      :disabled="dimTesting"
                      @click="testDimConnection"
                    >
                      <b-spinner
                        v-if="dimTesting"
                        small
                      />
                      <feather-icon
                        v-else
                        icon="ZapIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ $t("node.admin.testConnectionButton") }}
                    </b-button>
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      variant="primary"
                      size="sm"
                      :disabled="dimSaving"
                      @click="saveDimConfig"
                    >
                      <b-spinner
                        v-if="dimSaving"
                        small
                      />
                      <feather-icon
                        v-else
                        icon="SaveIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ $t("common.save") }}
                    </b-button>
                    <b-button
                      v-if="$hasAbility('node.ver')"
                      variant="outline-primary"
                      size="sm"
                      :to="{
                        name: 'NodeMaterials',
                        params: { nodeId: node.id },
                      }"
                    >
                      <feather-icon
                        icon="PackageIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ $t("node.admin.manageMaterialsButton") }}
                    </b-button>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ── Aba: Integração com o Ares Frotas ── -->
      <b-tab :title="$t('node.admin.frotasTabTitle')">
        <div class="row">
          <div class="col-md-7">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">{{ $t("node.admin.frotasTitle") }}</span>
              </div>
              <div class="card-body">
                <div
                  v-if="frotasLoading"
                  class="text-center py-2"
                >
                  <b-spinner small /> {{ $t("node.admin.loadingLabel") }}
                </div>
                <template v-else>
                  <p class="text-muted small">
                    {{ $t("node.admin.frotasHint") }}
                  </p>

                  <div class="form-group">
                    <label>{{ $t("node.admin.frotasBaseUrlLabel") }}</label>
                    <input
                      v-model="frotas.base_url"
                      class="form-control"
                      type="text"
                      placeholder="https://api.frotas.exemplo.com.br"
                    >
                  </div>
                  <div class="form-group">
                    <label>{{
                      frotas.has_secret
                        ? $t("node.admin.frotasSecretKeepLabel")
                        : $t("node.admin.frotasSecretLabel")
                    }}</label>
                    <input
                      v-model="frotas.secret"
                      class="form-control"
                      type="password"
                      autocomplete="new-password"
                      placeholder="••••••••"
                    >
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label>{{ $t("node.admin.frotasWsHostLabel") }}</label>
                      <input
                        v-model="frotas.ws_host"
                        class="form-control"
                        type="text"
                        placeholder="ws.frotas.exemplo.com.br"
                      >
                    </div>
                    <div class="form-group col-md-3">
                      <label>{{ $t("node.admin.frotasWsPortLabel") }}</label>
                      <input
                        v-model.number="frotas.ws_port"
                        class="form-control"
                        type="number"
                        min="1"
                        max="65535"
                      >
                    </div>
                    <div class="form-group col-md-3">
                      <label>{{ $t("node.admin.frotasWsSchemeLabel") }}</label>
                      <b-form-select
                        v-model="frotas.ws_scheme"
                        :options="['https', 'http']"
                      />
                    </div>
                  </div>
                  <div class="form-group">
                    <label>{{ $t("node.admin.frotasWsKeyLabel") }}</label>
                    <input
                      v-model="frotas.ws_key"
                      class="form-control"
                      type="text"
                    >
                  </div>
                  <div class="custom-control custom-switch mb-1">
                    <input
                      id="frotas-enabled"
                      v-model="frotas.enabled"
                      type="checkbox"
                      class="custom-control-input"
                    >
                    <label
                      class="custom-control-label"
                      for="frotas-enabled"
                    >{{ $t("node.admin.frotasEnabledLabel") }}</label>
                  </div>

                  <div
                    v-if="frotasTestResult"
                    class="frotas-test-result mb-1"
                  >
                    <div :class="frotasTestResult.api.ok ? 'text-success' : 'text-danger'">
                      <feather-icon
                        :icon="frotasTestResult.api.ok ? 'CheckCircleIcon' : 'XCircleIcon'"
                        size="14"
                        class="mr-25"
                      />
                      <strong>{{ $t("node.admin.frotasTestApi") }}:</strong> {{ frotasTestResult.api.message }}
                    </div>
                    <div :class="frotasTestResult.websocket.ok ? 'text-success' : 'text-danger'">
                      <feather-icon
                        :icon="frotasTestResult.websocket.ok ? 'CheckCircleIcon' : 'XCircleIcon'"
                        size="14"
                        class="mr-25"
                      />
                      <strong>{{ $t("node.admin.frotasTestWs") }}:</strong> {{ frotasTestResult.websocket.message }}
                    </div>
                  </div>

                  <div
                    class="d-flex flex-wrap"
                    style="gap: 8px"
                  >
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      variant="outline-secondary"
                      size="sm"
                      :disabled="frotasTesting"
                      @click="testFrotasConnection"
                    >
                      <b-spinner
                        v-if="frotasTesting"
                        small
                      />
                      <feather-icon
                        v-else
                        icon="ZapIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ $t("node.admin.testConnectionButton") }}
                    </b-button>
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      variant="primary"
                      size="sm"
                      :disabled="frotasSaving"
                      @click="saveFrotasConfig"
                    >
                      <b-spinner
                        v-if="frotasSaving"
                        small
                      />
                      <feather-icon
                        v-else
                        icon="SaveIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ $t("common.save") }}
                    </b-button>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Situação: quem decide para este nó e como está a conexão -->
          <div class="col-md-5">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">{{ $t("node.admin.frotasStatusTitle") }}</span>
              </div>
              <div class="card-body">
                <div
                  v-if="frotasLoading"
                  class="text-center py-2"
                >
                  <b-spinner small />
                </div>
                <template v-else>
                  <p
                    v-if="!frotasEffective"
                    class="mb-1 text-warning"
                  >
                    {{ $t("node.admin.frotasNone") }}
                  </p>
                  <p
                    v-else-if="frotasEffective.source === 'env'"
                    class="mb-1"
                  >
                    {{ $t("node.admin.frotasFromEnv") }}
                  </p>
                  <p
                    v-else-if="frotasEffective.inherited"
                    class="mb-1"
                  >
                    {{ $t("node.admin.frotasInherited", { node: frotasEffective.owner_node_name }) }}
                    <span
                      v-if="!frotasEffective.enabled"
                      class="text-danger"
                    >({{ $t("node.admin.frotasDisabled") }})</span>
                  </p>
                  <p
                    v-else
                    class="mb-1"
                  >
                    {{ frotasEffective.enabled ? $t("node.admin.frotasOwnEnabled") : $t("node.admin.frotasOwnDisabled") }}
                  </p>

                  <ul
                    v-if="frotas.id"
                    class="list-unstyled mb-0 small"
                  >
                    <li>
                      <span class="text-muted">{{ $t("node.admin.frotasLastConnected") }}:</span>
                      {{ frotasDate(frotas.last_connected_at) }}
                    </li>
                    <li>
                      <span class="text-muted">{{ $t("node.admin.frotasLastPosition") }}:</span>
                      {{ frotasDate(frotas.last_position_at) }}
                    </li>
                    <li
                      v-if="frotas.last_error"
                      class="text-danger mt-50"
                    >
                      {{ frotas.last_error }}
                    </li>
                  </ul>
                </template>
              </div>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <!-- Modal: mapa de toda a árvore de cobertura -->
    <b-modal
      id="modal-tree-map"
      :title="$t('node.admin.treeMapModalTitle')"
      size="xl"
      hide-footer
      @hide="treeMapPolygons = []"
    >
      <div style="height: 600px; position: relative">
        <div
          v-if="treeMapLoading"
          class="d-flex align-items-center justify-content-center"
          style="height: 100%"
        >
          <b-spinner variant="primary" />
          <span class="ml-2">{{ $t("node.admin.loadingPolygonsLabel") }}</span>
        </div>
        <gmap-map
          v-else
          :center="treeMapCenter"
          :zoom="treeMapZoom"
          style="width: 100%; height: 100%"
          :options="{ streetViewControl: false, mapTypeControl: false }"
        >
          <gmap-polygon
            v-for="(poly, idx) in treeMapPolygons"
            :key="idx"
            :paths="poly.paths"
            :options="{
              fillColor: poly.color,
              fillOpacity: 0.2,
              strokeColor: poly.color,
              strokeOpacity: 0.9,
              strokeWeight: 2,
            }"
          />
        </gmap-map>
      </div>
      <!-- Legenda por nó -->
      <div
        v-if="treeMapLegend.length"
        class="mt-1 d-flex flex-wrap gap-1"
      >
        <b-badge
          v-for="item in treeMapLegend"
          :key="item.nodeId"
          :style="`background:${item.color};`"
          class="mr-50 mb-50"
        >
          {{ item.nodeName }}
        </b-badge>
      </div>
    </b-modal>

    <div
      v-if="!node.id && loading"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
    </div>
  </div>
</template>

<script>
import {
  BTabs,
  BTab,
  BButton,
  BModal,
  BSpinner,
  BBadge,
  BFormCheckbox,
  BFormSelect,
} from 'bootstrap-vue'
import moment from 'moment'
import vSelect from 'vue-select'
import swal from 'sweetalert'
import Pagination from 'vue-pagination-2'
import opcoesPaginacao from '@/libs/paginationOptions'
import EquipmentList from '@/views/Equipment/Index.vue'
import DdiSelect from '@/components/DdiSelect.vue'
import TeamList from '@/views/Team/Index.vue'
import TeamTypeList from '@/views/TeamType/Index.vue'

// Chave de cada aba, na ordem em que aparecem no template. O lazy load usa a
// chave, não a posição — reordenar abas só exige atualizar esta lista.
const TAB_KEYS = [
  'details',
  'children',
  'company',
  'coverage',
  'disciplines',
  'professionals',
  'teams',
  'equipment',
  'materials',
  'frotas',
]

const LOGO_MAX_BYTES = 2 * 1024 * 1024 // mesmo limite do servidor

export default {
  components: {
    DdiSelect,
    EquipmentList,
    TeamList,
    TeamTypeList,
    BTabs,
    BTab,
    BButton,
    BModal,
    BSpinner,
    BBadge,
    BFormCheckbox,
    BFormSelect,
    vSelect,
    Pagination,
  },

  data() {
    return {
      logoPending: null, // { file, url }: logo escolhida e ainda não enviada
      loading: true,
      node: {},
      fieldSchema: [],

      nodeTypes: [],
      allNodes: [],

      // controle de lazy load por aba, pela chave (ver TAB_KEYS)
      tabsLoaded: {},

      // Integração de materiais (DIM)
      dim: {
        base_url: '',
        username: '',
        password: '',
        enabled: false,
        has_password: false,
      },
      // Integração com o Ares Frotas
      frotas: {
        id: null,
        base_url: '',
        secret: '',
        ws_host: '',
        ws_port: 443,
        ws_scheme: 'https',
        ws_key: '',
        enabled: false,
        has_secret: false,
        last_connected_at: null,
        last_position_at: null,
        last_error: null,
      },
      frotasEffective: null,
      frotasTestResult: null,
      frotasLoading: false,
      frotasSaving: false,
      frotasTesting: false,
      dimLoading: false,
      dimSaving: false,
      dimTesting: false,

      // sub-nós
      newChildId: null,

      // profissionais
      nodeProfessionals: [],
      nodeProfessionalsFilters: {
        professionalName: '',
        professionalTypeId: null,
        roleId: null,
      },
      nodeProfessionalsPagination: {
        current_page: 1,
        per_page: 10,
        pages: 1,
        count: 0,
      },
      nodeProfessionalsSort: { by: 'name', dir: 'asc' },
      nodeProfessionalsSearchTimer: null,
      allProfessionals: [],
      allRoles: [],
      allProfessionalTypes: [],
      newProfessional: {
        professional_id: null,
        role_id: null,
        professional_type_id: null,
        starts_at: '',
        ends_at: '',
      },
      editingLink: { id: null, starts_at: '', ends_at: '' },

      // empresa
      company: {
        name: '',
        fantasy_name: '',
        cnpj: '',
        type: '',
        phone: '',
        cep: '',
        address: '',
        neighborhood: '',
        number: '',
        complement: '',
        reference: '',
        state_id: null,
        city_id: null,
        status: 1,
      },
      ufs: [],
      cities: [],

      // abrangência
      coverages: [],
      coveragesLoading: false,
      ufsForCoverage: [],
      coverageUfFilter: null,
      citiesForCoverage: [],
      newCoverage: {
        coverage_type: '',
        uf_id: null,
        city_id: null,
        neighborhood_name: '',
      },
      parentCoverages: [],
      mapCenter: { lat: -15.77972, lng: -47.92972 },
      mapZoom: 4,
      mapPolygons: [],
      newCoverageViewport: null, // viewport do Google para bairro selecionado

      // modal mapa árvore
      treeMapLoading: false,
      treeMapPolygons: [],
      treeMapLegend: [],
      treeMapCenter: { lat: -15.77972, lng: -47.92972 },
      treeMapZoom: 4,

      // disciplinas
      allDisciplines: [],
      selectedDisciplineIds: [],
      disciplineConfig: {},
    }
  },

  computed: {
    // A logo em prévia (ainda não salva) tem prioridade sobre a salva.
    logoPreviewUrl() {
      return this.logoPending ? this.logoPending.url : this.node.logo_url
    },
    addressSearchModeOptions() {
      return [
        { value: 'free', text: this.$t('node.form.addressSearchModeFree') },
        { value: 'prioritize', text: this.$t('node.form.addressSearchModePrioritize') },
        { value: 'only', text: this.$t('node.form.addressSearchModeOnly') },
      ]
    },
    opcoesPaginacao() {
      return opcoesPaginacao
    },

    // Deriva sub-nós do allNodes (evita request extra ao servidor)
    children() {
      if (!this.node.id) return []
      return this.allNodes.filter(n => n.parent_id == this.node.id)
    },

    availableChildren() {
      const linkedIds = this.children.map(c => c.id)
      return this.allNodes.filter(
        n => n.id !== this.node.id && !linkedIds.includes(n.id),
      )
    },
    availableProfessionals() {
      // Vínculo encerrado (ends_at no passado) não conta como "vinculado" —
      // só um vínculo vigente tira o profissional da lista de disponíveis.
      const today = new Date().toISOString().slice(0, 10)
      const linkedIds = this.nodeProfessionals
        .filter(
          np => np.starts_at <= today && (!np.ends_at || np.ends_at >= today),
        )
        .map(np => np.professional_id)
      return this.allProfessionals.filter(p => !linkedIds.includes(p.id))
    },

    canAddCoverage() {
      const t = this.newCoverage.coverage_type
      if (!t) return false
      if (t === 'state') return !!this.newCoverage.uf_id
      if (t === 'city') return !!this.newCoverage.city_id
      if (t === 'neighborhood') return !!this.newCoverage.neighborhood_name
      return false
    },

    // Restrição herdada das coberturas do nó pai
    parentConstraint() {
      if (!this.parentCoverages.length) return null

      const types = this.parentCoverages.map(c => c.coverage_type)

      const stateUfIds = [
        ...new Set(
          this.parentCoverages
            .filter(c => c.coverage_type === 'state' && c.uf_id)
            .map(c => c.uf_id),
        ),
      ]
      const cityUfIds = [
        ...new Set(
          this.parentCoverages
            .filter(c => c.coverage_type === 'city' && c.uf_id)
            .map(c => c.uf_id),
        ),
      ]

      if (types.includes('city')) {
        const cityNames = this.parentCoverages
          .filter(c => c.coverage_type === 'city' && c.city)
          .map(c => {
            const uf = c.uf ? c.uf.code : null
            return uf ? `${c.city.name}/${uf}` : c.city.name
          })
        return {
          allowedTypes: ['neighborhood'],
          lockedUfIds: cityUfIds,
          neighborhoodHint: cityNames.length
            ? this.$t('node.admin.messages.selectNeighborhoodWithinCities', {
              cities: cityNames.join(', '),
            })
            : this.$t(
              'node.admin.messages.selectNeighborhoodWithinParentCities',
            ),
          message: this.$t('node.admin.messages.parentCoversCitiesMessage'),
        }
      }

      if (types.includes('state')) {
        const ufNames = this.parentCoverages
          .filter(c => c.coverage_type === 'state' && c.uf)
          .map(c => c.uf.code || c.uf.name)
        return {
          allowedTypes: ['city', 'neighborhood'],
          lockedUfIds: stateUfIds,
          neighborhoodHint: ufNames.length
            ? this.$t('node.admin.messages.selectNeighborhoodWithinStates', {
              states: ufNames.join(', '),
            })
            : null,
          message: ufNames.length
            ? this.$t('node.admin.messages.parentCoversStatesMessage', {
              states: ufNames.join(', '),
            })
            : this.$t('node.admin.messages.parentCoversStatesGenericMessage'),
        }
      }

      return null
    },

    // Opções de tipo de cobertura filtradas pela restrição do pai
    coverageTypeOptions() {
      const all = [
        {
          label: this.$t('node.admin.coverageTypeLabels.state'),
          value: 'state',
        },
        { label: this.$t('node.admin.coverageTypeLabels.city'), value: 'city' },
        {
          label: this.$t('node.admin.coverageTypeLabels.neighborhood'),
          value: 'neighborhood',
        },
      ]
      if (!this.parentConstraint) return all
      return all.filter(o => this.parentConstraint.allowedTypes.includes(o.value))
    },

    // UFs disponíveis para o formulário de cobertura (filtradas pelo pai se necessário)
    ufsForCoverageFiltered() {
      if (!this.parentConstraint || !this.parentConstraint.lockedUfIds.length) {
        return this.ufsForCoverage
      }
      return this.ufsForCoverage.filter(u => this.parentConstraint.lockedUfIds.includes(u.id))
    },

    // Bounding box derivado da cobertura do pai (para bias do autocomplete de bairro)
    parentCoverageBounds() {
      if (!this.parentCoverages.length) return null
      const cov = this.parentCoverages.find(
        c => c.coverage_type === 'city' && c.geojson,
      )
        || this.parentCoverages.find(
          c => c.coverage_type === 'state' && c.geojson,
        )
      if (!cov) return null
      try {
        const geo = typeof cov.geojson === 'string'
          ? JSON.parse(cov.geojson)
          : cov.geojson
        let coords = []
        if (geo.type === 'Polygon') coords = geo.coordinates[0]
        else if (geo.type === 'MultiPolygon') coords = geo.coordinates[0][0]
        if (!coords.length) return null
        const lngs = coords.map(c => c[0])
        const lats = coords.map(c => c[1])
        return {
          north: Math.max(...lats),
          south: Math.min(...lats),
          east: Math.max(...lngs),
          west: Math.min(...lngs),
        }
      } catch {
        return null
      }
    },

    neighborhoodAutocompleteOptions() {
      const opts = {
        types: ['neighborhood', 'sublocality', 'sublocality_level_1'],
        componentRestrictions: { country: 'br' },
        fields: ['name', 'geometry', 'address_components', 'formatted_address'],
      }
      if (this.parentCoverageBounds) {
        opts.bounds = this.parentCoverageBounds
        opts.strictBounds = false
      }
      return opts
    },
  },

  beforeDestroy() {
    this.cancelLogo()
  },

  mounted() {
    this.loadAll()
  },

  methods: {
    // ── carregamentos ─────────────────────────────────────────────────────

    loadAll() {
      const { nodeId: id } = this.$route.params
      this.loading = true
      // Reseta lazy load ao navegar entre nós
      this.tabsLoaded = {}
      // Carrega dados da aba inicial + contadores de todas as abas
      Promise.all([
        this.loadNode(id),
        this.loadNodeTypes(),
        this.loadAllNodes(), // já inclui with[]=type, sub-nós são derivados daqui
        this.loadNodeProfessionals(id),
        this.loadCoverages(id),
        this.loadNodeDisciplines(id),
      ]).finally(() => {
        this.loading = false
        this.$loading(false)
      })
    },

    loadNode(id) {
      return axios
        .get(`${this.$store.state.api}nodes/${id}?with[]=type&with[]=parent`)
        .then(r => {
          const data = r.data.data[0]
          data.metadata = data.metadata || {}
          this.node = data
          // Carrega schema do tipo assim que o nó e os tipos estiverem disponíveis
          this._applyFieldSchema()
          // Carrega coberturas do pai para encadeamento de abrangência
          if (data.parent_id) {
            this.loadParentCoverages(data.parent_id)
          } else {
            this.parentCoverages = []
          }
        })
    },

    loadNodeTypes() {
      return axios
        .get(`${this.$store.state.api}node_types?paginated=false`)
        .then(r => {
          this.nodeTypes = r.data.data
          this._applyFieldSchema()
        })
    },

    _applyFieldSchema() {
      if (!this.node.type_id || !this.nodeTypes.length) return
      const type = this.nodeTypes.find(t => t.id === this.node.type_id)
      this.fieldSchema = type && type.field_schema ? type.field_schema : []
      const meta = this.node.metadata || {}
      this.fieldSchema.forEach(f => {
        if (!(f.key in meta)) meta[f.key] = ''
      })
      this.node.metadata = meta
    },

    loadAllNodes() {
      // Inclui type para que sub-nós derivados já tenham o relacionamento
      return axios
        .get(`${this.$store.state.api}nodes?paginated=false&with[]=type`)
        .then(r => {
          this.allNodes = r.data.data
        })
    },

    loadNodeProfessionals(id, page = 1) {
      const f = this.nodeProfessionalsFilters
      const s = this.nodeProfessionalsSort
      let query = `node_professionals?paginated=true&limit=${this.nodeProfessionalsPagination.per_page}&page=${page}&node_id=${id}&with[]=professional&with[]=role&with[]=professionalType&sort=${s.by}&sort_dir=${s.dir}`

      if (f.professionalName) {
        query += `&professional[name]=${encodeURIComponent(
          f.professionalName,
        )}`
      }
      if (f.professionalTypeId) query += `&professional_type_id=${f.professionalTypeId}`
      if (f.roleId) query += `&role_id=${f.roleId}`

      return axios.get(`${this.$store.state.api}${query}`).then(r => {
        this.nodeProfessionals = r.data.data
        this.nodeProfessionalsPagination.current_page = page
        this.nodeProfessionalsPagination.pages = r.data.pages || 1
        this.nodeProfessionalsPagination.count = r.data.count || 0
      })
    },

    changeNodeProfessionalsPage(page) {
      this.loadNodeProfessionals(this.node.id, page)
    },

    onNodeProfessionalsFilterChange() {
      clearTimeout(this.nodeProfessionalsSearchTimer)
      this.nodeProfessionalsSearchTimer = setTimeout(() => {
        this.loadNodeProfessionals(this.node.id, 1)
      }, 400)
    },

    toggleNodeProfessionalsSort(column) {
      if (this.nodeProfessionalsSort.by === column) {
        this.nodeProfessionalsSort.dir = this.nodeProfessionalsSort.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.nodeProfessionalsSort.by = column
        this.nodeProfessionalsSort.dir = 'asc'
      }
      this.loadNodeProfessionals(this.node.id, 1)
    },

    loadAllProfessionalTypes() {
      return axios
        .get(
          `${this.$store.state.api}professional_types?paginated=false&status=1`,
        )
        .then(r => {
          this.allProfessionalTypes = r.data.data
        })
    },

    loadAllRoles() {
      return axios
        .get(`${this.$store.state.api}roles?paginated=false&status=1`)
        .then(r => {
          this.allRoles = r.data.data
        })
    },

    loadAllProfessionals() {
      return axios
        .get(`${this.$store.state.api}professionals?paginated=false&status=1`)
        .then(r => {
          this.allProfessionals = r.data.data
        })
    },

    // ── empresa ───────────────────────────────────────────────────────────

    loadNodeCompany(id) {
      return axios
        .get(`${this.$store.state.api}companies?paginated=false&node_id=${id}`)
        .then(r => {
          const list = r.data.data || []
          if (list.length > 0) {
            this.company = list[0]
            if (this.company.state_id) this.getCities()
          } else {
            this.company = {
              name: '',
              fantasy_name: '',
              cnpj: '',
              type: '',
              phone: '',
              cep: '',
              address: '',
              neighborhood: '',
              number: '',
              complement: '',
              reference: '',
              state_id: null,
              city_id: null,
              status: 1,
            }
          }
        })
    },

    loadUfs() {
      return axios
        .get(`${this.$store.state.api}ufs?paginated=false`)
        .then(r => {
          this.ufs = r.data.data
        })
    },

    getCities() {
      if (!this.company.state_id) {
        this.cities = []
        return Promise.resolve()
      }
      return axios
        .get(
          `${this.$store.state.api}cities?paginated=false&uf_id=${this.company.state_id}`,
        )
        .then(r => {
          this.cities = r.data.data
        })
    },

    buscarCep() {
      const self = this
      if (!/^[0-9]{5}-[0-9]{3}$/.test(this.company.cep)) return
      $.getJSON(
        `https://viacep.com.br/ws/${this.company.cep}/json/`,
        endereco => {
          if (endereco.erro) return
          self.company.address = endereco.logradouro
          self.company.neighborhood = endereco.bairro
          const api = `${
            self.$store.state.api
          }cities?code=${endereco.ibge.slice(0, -1)}`
          axios.get(api).then(r => {
            self.company.state_id = r.data.data[0].uf_id
            self.getCities().then(() => {
              self.company.city_id = r.data.data[0].id
            })
          })
          $('#inputNumeroEmpresa').focus()
        },
      )
    },

    saveCompany() {
      const self = this
      const api = `${self.$store.state.api}companies`
      const payload = { ...self.company, node_id: self.node.id }

      self.$loading(true)

      if (self.company.id) {
        payload._method = 'PUT'
        axios
          .post(`${api}/${self.company.id}`, payload)
          .then(() => {
            self.$message(
              self.$t('node.messages.successTitle'),
              self.$t('node.admin.messages.companyUpdated'),
              'success',
            )
            self.$loading(false)
          })
          .catch(e => {
            self.$loading(false)
            self.$message(null, e.response.data, 'error')
          })
      } else {
        axios
          .post(api, payload)
          .then(r => {
            self.company = r.data.data || { ...self.company, ...r.data }
            self.$message(
              self.$t('node.messages.successTitle'),
              self.$t('node.admin.messages.companyCreated'),
              'success',
            )
            self.$loading(false)
            self.loadNodeCompany(self.node.id)
          })
          .catch(e => {
            self.$loading(false)
            self.$message(null, e.response.data, 'error')
          })
      }
    },

    unlinkCompany() {
      if (!confirm(this.$t('node.admin.messages.confirmUnlinkCompany'))) return
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}companies/${this.company.id}`, {
          ...this.company,
          node_id: null,
          _method: 'PUT',
        })
        .then(() => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.companyUnlinked'),
            'success',
          )
          this.$loading(false)
          this.loadNodeCompany(this.node.id)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── detalhes do nó ────────────────────────────────────────────────────

    // Escolher o arquivo só mostra a prévia no topo da folha; a logo só vai para o servidor ao confirmar.
    pickLogo(event) {
      const file = event.target.files[0]
      // eslint-disable-next-line no-param-reassign
      event.target.value = ''
      if (!file) return
      if (!['image/png', 'image/jpeg'].includes(file.type)) {
        this.$message(null, this.$t('node.form.logoInvalidType'), 'error')
        return
      }
      if (file.size > LOGO_MAX_BYTES) {
        this.$message(null, this.$t('node.form.logoTooLarge'), 'error')
        return
      }
      this.cancelLogo()
      this.logoPending = { file, url: URL.createObjectURL(file) }
    },

    cancelLogo() {
      if (this.logoPending) URL.revokeObjectURL(this.logoPending.url)
      this.logoPending = null
    },

    confirmLogo() {
      if (!this.logoPending) return
      const fd = new FormData()
      fd.append('logo', this.logoPending.file)
      this.$loading(true)
      this.$http
        .post(`${this.$store.state.api}nodes/${this.node.id}/logo`, fd)
        .then(r => {
          this.$set(this.node, 'logo_url', r.data.logo_url)
          this.cancelLogo()
          this.$message(this.$t('node.messages.successTitle'), this.$t('node.form.logoSaved'), 'success')
        })
        .catch(() => this.$message(null, this.$t('node.form.logoUploadFailed'), 'error'))
        .finally(() => this.$loading(false))
    },

    removeLogo() {
      this.$loading(true)
      this.$http
        .delete(`${this.$store.state.api}nodes/${this.node.id}/logo`)
        .then(() => {
          this.$set(this.node, 'logo_url', null)
          this.$message(this.$t('node.messages.successTitle'), this.$t('node.form.logoRemoved'), 'success')
        })
        .catch(() => this.$message(null, this.$t('node.form.logoUploadFailed'), 'error'))
        .finally(() => this.$loading(false))
    },

    saveNode() {
      this.$loading(true)
      this.$http
        .post(`${this.$store.state.api}nodes/${this.node.id}`, {
          ...this.node,
          _method: 'PUT',
        })
        .then(() => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.nodeUpdated'),
            'success',
          )
          this.$loading(false)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── sub-nós ───────────────────────────────────────────────────────────

    goToAdmin(id) {
      this.$loading(true)
      this.$router.push(`/nodes/admin/${id}`)
    },

    openAddChild() {
      this.newChildId = null
      this.$bvModal.show('modal-add-child')
    },

    saveChild() {
      if (!this.newChildId) return
      const child = this.allNodes.find(n => n.id === this.newChildId)
      if (!child) return

      this.$loading(true)
      this.$http
        .post(`${this.$store.state.api}nodes/${this.newChildId}`, {
          ...child,
          parent_id: this.node.id,
          _method: 'PUT',
        })
        .then(() => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.subNodeLinked'),
            'success',
          )
          this.$loading(false)
          this.loadAllNodes()
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    unlinkChild(child) {
      if (
        !confirm(
          this.$t('node.admin.messages.confirmRemoveChild', {
            name: child.name,
          }),
        )
      ) return
      this.$loading(true)
      this.$http
        .post(`${this.$store.state.api}nodes/${child.id}`, {
          ...child,
          parent_id: null,
          _method: 'PUT',
        })
        .then(() => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.subNodeUnlinked'),
            'success',
          )
          this.$loading(false)
          this.loadAllNodes()
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── profissionais ─────────────────────────────────────────────────────

    openAddProfessional() {
      this.newProfessional = {
        professional_id: null,
        role_id: null,
        professional_type_id: null,
        starts_at: this.todayIso(),
        ends_at: '',
      }
      this.$bvModal.show('modal-add-professional')
    },

    saveProfessional() {
      if (!this.newProfessional.professional_id) return
      this.$loading(true)
      this.$http
        .post(`${this.$store.state.api}node_professionals`, {
          node_id: this.node.id,
          professional_id: this.newProfessional.professional_id,
          role_id: this.newProfessional.role_id,
          professional_type_id: this.newProfessional.professional_type_id,
          starts_at: this.newProfessional.starts_at,
          ends_at: this.newProfessional.ends_at || null,
        })
        .then(() => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.professionalLinked'),
            'success',
          )
          this.$loading(false)
          this.loadNodeProfessionals(
            this.node.id,
            this.nodeProfessionalsPagination.current_page,
          )
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    todayIso() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        '0',
      )}-${String(d.getDate()).padStart(2, '0')}`
    },

    formatIsoDate(value) {
      if (!value) return ''
      const [ano, mes, dia] = String(value).slice(0, 10).split('-')
      return `${dia}/${mes}/${ano}`
    },

    linkStatus(np) {
      const today = this.todayIso()
      const starts = np.starts_at ? String(np.starts_at).slice(0, 10) : null
      const ends = np.ends_at ? String(np.ends_at).slice(0, 10) : null
      if (starts && today < starts) {
        return {
          label: this.$t('node.admin.linkStatus.future'),
          variant: 'info',
        }
      }
      if (ends && today > ends) {
        return {
          label: this.$t('node.admin.linkStatus.ended'),
          variant: 'secondary',
        }
      }
      return {
        label: this.$t('node.admin.linkStatus.active'),
        variant: 'success',
      }
    },

    openEditLink(np) {
      this.editingLink = {
        id: np.id,
        node_id: np.node_id,
        professional_id: np.professional_id,
        role_id: np.role_id,
        professional_type_id: np.professional_type_id,
        starts_at: np.starts_at ? String(np.starts_at).slice(0, 10) : '',
        ends_at: np.ends_at ? String(np.ends_at).slice(0, 10) : '',
      }
      this.$bvModal.show('modal-edit-link')
    },

    saveEditLink() {
      this.$loading(true)
      this.$http
        .put(
          `${this.$store.state.api}node_professionals/${this.editingLink.id}`,
          {
            ...this.editingLink,
            ends_at: this.editingLink.ends_at || null,
          },
        )
        .then(() => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.validityUpdated'),
            'success',
          )
          this.$loading(false)
          this.loadNodeProfessionals(
            this.node.id,
            this.nodeProfessionalsPagination.current_page,
          )
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── lazy load por aba ──────────────────────────────────────────────────

    onTabChange(tabIndex) {
      const key = TAB_KEYS[tabIndex]
      if (!key || this.tabsLoaded[key]) return
      this.tabsLoaded[key] = true

      const { nodeId: id } = this.$route.params
      if (key === 'company') {
        Promise.all([this.loadNodeCompany(id), this.loadUfs()])
      } else if (key === 'professionals') {
        this.loadAllProfessionals()
        this.loadAllRoles()
        this.loadAllProfessionalTypes()
      } else if (key === 'disciplines') {
        this.loadAllDisciplines()
      } else if (key === 'coverage') {
        this.loadUfsForCoverage()
      } else if (key === 'materials') {
        this.loadDimConfig()
      } else if (key === 'frotas') {
        this.loadFrotasConfig()
      }
    },

    // ── Integração de materiais (DIM) ──────────────────────────────────────
    loadDimConfig() {
      this.dimLoading = true
      this.$http
        .get(`${this.$store.state.api}materials/config`, {
          params: { node_id: this.node.id },
        })
        .then(r => {
          const c = r.data.data
          this.dim = c
            ? {
              base_url: c.base_url || '',
              username: c.username || '',
              password: '',
              enabled: !!c.enabled,
              has_password: !!c.has_password,
            }
            : {
              base_url: '',
              username: '',
              password: '',
              enabled: false,
              has_password: false,
            }
        })
        // Sem aviso o formulário parecia "sem configuração salva" quando na verdade a carga falhava.
        .catch(() => this.$message(null, this.$t('node.admin.messages.dimConfigLoadError'), 'error'))
        .finally(() => {
          this.dimLoading = false
        })
    },

    saveDimConfig() {
      this.dimSaving = true
      const payload = {
        node_id: this.node.id,
        base_url: this.dim.base_url,
        username: this.dim.username,
        enabled: this.dim.enabled,
      }
      if (this.dim.password) payload.password = this.dim.password
      this.$http
        .post(`${this.$store.state.api}materials/config`, payload)
        .then(r => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.dimConfigSaved'),
            'success',
          )
          this.dim.has_password = !!(r.data.data && r.data.data.has_password)
          this.dim.password = ''
        })
        .catch(e => this.$message(
          null,
          (e.response && e.response.data)
              || this.$t('node.admin.messages.saveError'),
          'error',
        ))
        .finally(() => {
          this.dimSaving = false
        })
    },

    // ── Integração com o Ares Frotas ───────────────────────────────────────
    frotasPayload() {
      const payload = {
        node_id: this.node.id,
        base_url: this.frotas.base_url,
        ws_host: this.frotas.ws_host,
        ws_port: this.frotas.ws_port,
        ws_scheme: this.frotas.ws_scheme,
        ws_key: this.frotas.ws_key,
        enabled: this.frotas.enabled,
      }
      if (this.frotas.secret) payload.secret = this.frotas.secret

      return payload
    },

    applyFrotasResponse(data, effective) {
      const c = data || {}
      this.frotas = {
        id: c.id || null,
        base_url: c.base_url || '',
        secret: '',
        ws_host: c.ws_host || '',
        ws_port: c.ws_port || 443,
        ws_scheme: c.ws_scheme || 'https',
        ws_key: c.ws_key || '',
        enabled: !!c.enabled,
        has_secret: !!c.has_secret,
        last_connected_at: c.last_connected_at || null,
        last_position_at: c.last_position_at || null,
        last_error: c.last_error || null,
      }
      this.frotasEffective = effective || null
    },

    frotasDate(value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : this.$t('node.admin.frotasNever')
    },

    loadFrotasConfig() {
      this.frotasLoading = true
      this.frotasTestResult = null
      this.$http
        .get(`${this.$store.state.api}frotas/config`, { params: { node_id: this.node.id } })
        .then(r => this.applyFrotasResponse(r.data.data, r.data.effective))
        .catch(() => this.$message(null, this.$t('node.admin.messages.frotasConfigLoadError'), 'error'))
        .finally(() => {
          this.frotasLoading = false
        })
    },

    saveFrotasConfig() {
      this.frotasSaving = true
      this.$http
        .post(`${this.$store.state.api}frotas/config`, this.frotasPayload())
        .then(r => {
          this.applyFrotasResponse(r.data.data, r.data.effective)
          this.$message(null, this.$t('node.admin.messages.frotasConfigSaved'), 'success')
        })
        .catch(e => this.$message(
          null,
          (e.response && e.response.data && (e.response.data.message || Object.values(e.response.data)[0]))
            || this.$t('node.admin.messages.saveError'),
          'error',
        ))
        .finally(() => {
          this.frotasSaving = false
        })
    },

    testFrotasConnection() {
      this.frotasTesting = true
      this.frotasTestResult = null
      this.$http
        .post(`${this.$store.state.api}frotas/config/test`, this.frotasPayload())
        .then(r => {
          this.frotasTestResult = r.data
          this.$message(this.$t('node.admin.messages.connectionOkTitle'), this.$t('node.admin.messages.frotasTestOk'), 'success')
        })
        .catch(e => {
          // 422 traz o resultado detalhado (qual metade falhou); sem corpo útil, é falha de rede.
          const data = e.response && e.response.data
          if (data && data.api && data.websocket) {
            this.frotasTestResult = data
            this.$message(this.$t('node.admin.messages.failureTitle'), this.$t('node.admin.messages.frotasTestFail'), 'error')
          } else {
            this.$message(this.$t('node.admin.messages.failureTitle'), this.$t('node.admin.messages.cannotConnect'), 'error')
          }
        })
        .finally(() => {
          this.frotasTesting = false
        })
    },

    testDimConnection() {
      this.dimTesting = true
      const payload = {
        node_id: this.node.id,
        base_url: this.dim.base_url,
        username: this.dim.username,
      }
      if (this.dim.password) payload.password = this.dim.password
      this.$http
        .post(`${this.$store.state.api}materials/config/test`, payload)
        .then(r => {
          if (r.data && r.data.ok) {
            this.$message(
              this.$t('node.admin.messages.connectionOkTitle'),
              this.$t('node.admin.messages.dimAuthSuccess'),
              'success',
            )
          } else {
            this.$message(
              this.$t('node.admin.messages.failureTitle'),
              (r.data && r.data.message)
                || this.$t('node.admin.messages.cannotConnect'),
              'error',
            )
          }
        })
        .catch(() => this.$message(
          this.$t('node.admin.messages.failureTitle'),
          this.$t('node.admin.messages.cannotConnectDim'),
          'error',
        ))
        .finally(() => {
          this.dimTesting = false
        })
    },

    async unlinkProfessional(np) {
      const name = np.professional
        ? np.professional.name
        : this.$t('node.admin.professionalFallbackName')
      const ok = await swal({
        title: this.$t('node.admin.messages.confirmUnlinkProfessional', { name }),
        icon: 'warning',
        buttons: [this.$t('common.cancel'), this.$t('node.actions.unlink')],
        dangerMode: true,
      })
      if (!ok) return
      this.$loading(true)
      this.$http
        .delete(`${this.$store.state.api}node_professionals/${np.id}`)
        .then(() => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.professionalUnlinked'),
            'success',
          )
          this.$loading(false)
          this.loadNodeProfessionals(
            this.node.id,
            this.nodeProfessionalsPagination.current_page,
          )
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── abrangência ────────────────────────────────────────────────────────

    loadCoverages(id) {
      this.coveragesLoading = true
      return axios
        .get(
          `${this.$store.state.api}node_coverages?paginated=false&node_id=${id}&with[]=uf&with[]=city`,
        )
        .then(r => {
          this.coverages = r.data.data
          this.rebuildMapPolygons()
        })
        .finally(() => {
          this.coveragesLoading = false
        })
    },

    loadUfsForCoverage() {
      if (this.ufsForCoverage.length) return Promise.resolve()
      return axios
        .get(`${this.$store.state.api}ufs?paginated=false`)
        .then(r => {
          this.ufsForCoverage = r.data.data
        })
    },

    loadCitiesForCoverage() {
      // Sync uf_id so it gets persisted for city-type coverages
      this.newCoverage.uf_id = this.coverageUfFilter || null
      if (!this.coverageUfFilter) {
        this.citiesForCoverage = []
        return
      }
      axios
        .get(
          `${this.$store.state.api}cities?paginated=false&uf_id=${this.coverageUfFilter}`,
        )
        .then(r => {
          this.citiesForCoverage = r.data.data
          this.newCoverage.city_id = null
        })
    },

    onCoverageTypeChange() {
      this.newCoverage.uf_id = null
      this.newCoverage.city_id = null
      this.newCoverage.neighborhood_name = ''
      this.coverageUfFilter = null
      this.citiesForCoverage = []
      this.newCoverageViewport = null

      // Auto-aplicar UF travada quando o pai restringe a um único estado
      if (
        this.newCoverage.coverage_type === 'city'
        && this.parentConstraint
        && this.parentConstraint.lockedUfIds.length === 1
      ) {
        this.coverageUfFilter = this.parentConstraint.lockedUfIds[0]
        this.loadCitiesForCoverage()
      }
    },

    loadParentCoverages(parentId) {
      return axios
        .get(
          `${this.$store.state.api}node_coverages?paginated=false&node_id=${parentId}&with[]=uf&with[]=city`,
        )
        .then(r => {
          this.parentCoverages = r.data.data
        })
    },

    onNeighborhoodPlaceSelected(place) {
      if (!place || !place.geometry) return

      // ── Valida se o bairro está dentro da cobertura do nó pai ──────────
      if (this.parentConstraint && place.address_components) {
        const getComponent = types => {
          const comp = place.address_components.find(c => types.every(t => c.types.includes(t)))
          return comp ? comp.long_name : null
        }
        const getComponentShort = types => {
          const comp = place.address_components.find(c => types.every(t => c.types.includes(t)))
          return comp ? comp.short_name : null
        }

        // Estado do lugar selecionado (nome longo e abreviação)
        const placeStateLong = getComponent(['administrative_area_level_1'])
        const placeStateShort = getComponentShort([
          'administrative_area_level_1',
        ])
        // Município do lugar selecionado
        const placeCityLong = getComponent(['administrative_area_level_2'])

        // Pai com cobertura de município → valida estado E município
        const parentCities = this.parentCoverages.filter(
          c => c.coverage_type === 'city' && c.city,
        )
        if (parentCities.length) {
          const matchCity = parentCities.some(c => {
            const nameOk = c.city.name
              && placeCityLong
              && c.city.name
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                === placeCityLong
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()
            return nameOk
          })
          if (!matchCity) {
            const cityNames = parentCities.map(c => c.city.name).join(', ')
            this.$message(
              this.$t('node.admin.messages.invalidNeighborhoodTitle'),
              this.$t('node.admin.messages.selectNeighborhoodWithinCitiesMsg', {
                cities: cityNames,
              }),
              'error',
            )
            this.newCoverage.neighborhood_name = ''
            this.newCoverageViewport = null
            return
          }
        } else {
          // Pai com cobertura de estado → valida apenas estado
          const parentUfs = this.parentCoverages.filter(
            c => c.coverage_type === 'state' && c.uf,
          )
          if (parentUfs.length) {
            const matchState = parentUfs.some(
              c => (c.uf.code && c.uf.code === placeStateShort)
                || (c.uf.name
                  && placeStateLong
                  && c.uf.name
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()
                    === placeStateLong
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .toLowerCase()),
            )
            if (!matchState) {
              const ufNames = parentUfs
                .map(c => c.uf.code || c.uf.name)
                .join(', ')
              this.$message(
                this.$t('node.admin.messages.invalidNeighborhoodTitle'),
                this.$t(
                  'node.admin.messages.selectNeighborhoodWithinStatesMsg',
                  { states: ufNames },
                ),
                'error',
              )
              this.newCoverage.neighborhood_name = ''
              this.newCoverageViewport = null
              return
            }
          }
        }
      }

      this.newCoverage.neighborhood_name = place.name || place.formatted_address || ''
      // Salva o viewport do Google (bounding box) para exibir no mapa
      if (place.geometry.viewport) {
        const vp = place.geometry.viewport
        const ne = {
          lat: vp.getNorthEast().lat(),
          lng: vp.getNorthEast().lng(),
        }
        const sw = {
          lat: vp.getSouthWest().lat(),
          lng: vp.getSouthWest().lng(),
        }
        // Converte viewport em polígono retangular (GeoJSON)
        this.newCoverageViewport = {
          type: 'Polygon',
          coordinates: [
            [
              [sw.lng, sw.lat],
              [ne.lng, sw.lat],
              [ne.lng, ne.lat],
              [sw.lng, ne.lat],
              [sw.lng, sw.lat],
            ],
          ],
        }
        // Centraliza o mapa no lugar selecionado
        const loc = place.geometry.location
        this.mapCenter = { lat: loc.lat(), lng: loc.lng() }
        this.mapZoom = 14
      }
    },

    async addCoverage() {
      this.$loading(true)

      // Para estado e município, busca o polígono agora e salva no banco
      // (bairro já tem o viewport do Google em newCoverageViewport)
      let geojson = this.newCoverageViewport
        ? JSON.stringify(this.newCoverageViewport)
        : null

      if (!geojson) {
        let params = null
        if (
          this.newCoverage.coverage_type === 'state'
          && this.newCoverage.uf_id
        ) {
          const uf = this.ufsForCoverage.find(
            u => u.id === this.newCoverage.uf_id,
          )
          if (uf) params = { state: uf.name }
        } else if (
          this.newCoverage.coverage_type === 'city'
          && this.newCoverage.city_id
        ) {
          const city = this.citiesForCoverage.find(
            c => c.id === this.newCoverage.city_id,
          )
          const uf = this.ufsForCoverage.find(
            u => u.id === this.newCoverage.uf_id,
          )
          if (city) params = { city: city.name, ...(uf ? { state: uf.name } : {}) }
        }
        if (params) {
          const paths = await this.fetchPolygon(params)
          if (paths) {
            // Converte paths de volta para GeoJSON
            const toCoords = path => [
              ...path.map(({ lat, lng }) => [lng, lat]),
              [path[0].lng, path[0].lat],
            ]
            if (paths.length === 1) {
              geojson = JSON.stringify({
                type: 'Polygon',
                coordinates: [toCoords(paths[0])],
              })
            } else {
              geojson = JSON.stringify({
                type: 'MultiPolygon',
                coordinates: paths.map(p => [toCoords(p)]),
              })
            }
          }
        }
      }

      const payload = {
        node_id: this.node.id,
        coverage_type: this.newCoverage.coverage_type,
        uf_id: this.newCoverage.uf_id || null,
        city_id: this.newCoverage.city_id || null,
        neighborhood_name: this.newCoverage.neighborhood_name || null,
        geojson,
      }

      axios
        .post(`${this.$store.state.api}node_coverages`, payload)
        .then(() => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.coverageAdded'),
            'success',
          )
          this.$loading(false)
          this.newCoverage = {
            coverage_type: '',
            uf_id: null,
            city_id: null,
            neighborhood_name: '',
          }
          this.coverageUfFilter = null
          this.citiesForCoverage = []
          this.newCoverageViewport = null
          this.loadCoverages(this.node.id)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    removeCoverage(cov) {
      swal({
        title: this.$t('node.admin.messages.confirmRemoveCoverageTitle'),
        text: this.$t('node.admin.messages.confirmRemoveCoverageText', {
          description: this.coverageDescription(cov),
        }),
        icon: 'warning',
        buttons: {
          cancel: this.$t('common.cancel'),
          confirm: {
            text: this.$t('node.admin.removeButton'),
            className: 'btn-danger',
          },
        },
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}node_coverages/${cov.id}`)
          .then(() => {
            this.$message(
              this.$t('node.messages.successTitle'),
              this.$t('node.admin.messages.coverageRemoved'),
              'success',
            )
            this.$loading(false)
            this.loadCoverages(this.node.id)
          })
          .catch(e => {
            this.$loading(false)
            this.$message(null, e.response.data, 'error')
          })
      })
    },

    openTreeMap() {
      this.treeMapPolygons = []
      this.treeMapLegend = []
      this.treeMapLoading = true
      this.$bvModal.show('modal-tree-map')

      axios
        .get(`${this.$store.state.api}node_coverages_tree/${this.node.id}`)
        .then(r => {
          const coverages = r.data
          const toLatLng = coords => coords.map(([lng, lat]) => ({ lat, lng }))

          // Assign a distinct color per node
          const palette = [
            '#1976D2',
            '#388E3C',
            '#F57C00',
            '#7B1FA2',
            '#D32F2F',
            '#00796B',
            '#FBC02D',
            '#5D4037',
            '#0288D1',
            '#C2185B',
          ]
          const nodeColorMap = {}
          let colorIdx = 0

          const polygons = []
          const legendMap = {}

          coverages.forEach(cov => {
            if (!cov.geojson) return
            const nodeId = cov.node_id
            if (!nodeColorMap[nodeId]) {
              nodeColorMap[nodeId] = palette[colorIdx % palette.length]
              colorIdx++
              legendMap[nodeId] = {
                nodeId,
                nodeName: cov.node
                  ? cov.node.name
                  : this.$t('node.admin.nodeFallbackName', { id: nodeId }),
                color: nodeColorMap[nodeId],
              }
            }
            const color = nodeColorMap[nodeId]
            try {
              const geo = typeof cov.geojson === 'string'
                ? JSON.parse(cov.geojson)
                : cov.geojson
              let paths = null
              if (geo.type === 'Polygon') paths = [toLatLng(geo.coordinates[0])]
              else if (geo.type === 'MultiPolygon') paths = geo.coordinates.map(p => toLatLng(p[0]))
              if (paths) paths.forEach(p => polygons.push({ paths: p, color }))
            } catch {}
          })

          this.treeMapPolygons = polygons
          this.treeMapLegend = Object.values(legendMap)

          // Center on first polygon
          if (polygons.length && polygons[0].paths.length) {
            this.treeMapCenter = polygons[0].paths[0]
            this.treeMapZoom = 8
          }
        })
        .finally(() => {
          this.treeMapLoading = false
        })
    },

    coverageLabel(type) {
      return (
        {
          state: this.$t('node.admin.coverageTypeLabels.state'),
          city: this.$t('node.admin.coverageTypeLabels.city'),
          neighborhood: this.$t('node.admin.coverageTypeLabels.neighborhood'),
        }[type] || type
      )
    },

    coverageBadge(type) {
      return (
        { state: 'info', city: 'primary', neighborhood: 'success' }[type]
        || 'secondary'
      )
    },

    coverageDescription(cov) {
      // Helper: resolve UF code from cov.uf or fallback via city.uf_id
      const resolveUfCode = () => {
        if (cov.uf) return cov.uf.code
        if (cov.city && cov.city.uf_id) {
          const found = this.ufsForCoverage.find(
            u => u.id === cov.city.uf_id,
          )
          if (found) return found.code
        }
        return null
      }

      if (cov.coverage_type === 'state') return cov.uf ? cov.uf.name : cov.uf_id
      if (cov.coverage_type === 'city') {
        const city = cov.city ? cov.city.name : cov.city_id
        const uf = resolveUfCode()
        return uf ? `${city} / ${uf}` : city
      }
      if (cov.coverage_type === 'neighborhood') {
        const uf = resolveUfCode()
        return uf ? `${cov.neighborhood_name} / ${uf}` : cov.neighborhood_name
      }
      return '-'
    },

    // ── disciplinas ────────────────────────────────────────────────────────

    loadAllDisciplines() {
      return axios
        .get(`${this.$store.state.api}discipline?paginated=false&status=1`)
        .then(r => {
          this.allDisciplines = r.data.data
        })
    },

    loadNodeDisciplines(id) {
      return axios
        .get(`${this.$store.state.api}node_disciplines?node_id=${id}`)
        .then(r => {
          this.selectedDisciplineIds = r.data.data.map(Number)
        })
    },

    toggleDiscipline(id) {
      const idx = this.selectedDisciplineIds.indexOf(id)
      if (idx === -1) {
        this.selectedDisciplineIds.push(id)
      } else {
        this.selectedDisciplineIds.splice(idx, 1)
      }
    },

    openDisciplineConfig(disc) {
      this.$router.push(`/nodes/admin/${this.node.id}/discipline/${disc.id}`)
    },

    saveDisciplines() {
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}node_disciplines/sync`, {
          node_id: this.node.id,
          discipline_ids: this.selectedDisciplineIds,
        })
        .then(() => {
          this.$message(
            this.$t('node.messages.successTitle'),
            this.$t('node.admin.messages.disciplinesUpdated'),
            'success',
          )
          this.$loading(false)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // Busca polígono no Nominatim (OSM) e retorna array de paths para o GMap
    async fetchPolygon(params) {
      try {
        const base = 'https://nominatim.openstreetmap.org/search'
        const qs = new URLSearchParams({
          ...params,
          countrycodes: 'br',
          format: 'json',
          polygon_geojson: '1',
          limit: '5',
        })
        const res = await fetch(`${base}?${qs}`, {
          headers: { 'Accept-Language': 'pt-BR' },
        })
        const data = await res.json()
        if (!data.length) return null
        // Prefer entries that actually have a geojson polygon/multipolygon
        const withPoly = data.filter(
          d => d.geojson
            && (d.geojson.type === 'Polygon' || d.geojson.type === 'MultiPolygon'),
        )
        if (!withPoly.length) return null
        const geo = withPoly[0].geojson
        const toLatLng = coords => coords.map(([lng, lat]) => ({ lat, lng }))
        if (geo.type === 'Polygon') return [toLatLng(geo.coordinates[0])]
        if (geo.type === 'MultiPolygon') return geo.coordinates.map(poly => toLatLng(poly[0]))
        return null
      } catch {
        return null
      }
    },

    rebuildMapPolygons() {
      const colors = ['#1976D2', '#388E3C', '#F57C00', '#7B1FA2', '#D32F2F']
      const polygons = []
      const toLatLng = coords => coords.map(([lng, lat]) => ({ lat, lng }))
      let firstCenter = null
      let firstZoom = 4

      for (let i = 0; i < this.coverages.length; i++) {
        const cov = this.coverages[i]
        const color = colors[i % colors.length]
        if (!cov.geojson) continue
        try {
          const geo = typeof cov.geojson === 'string'
            ? JSON.parse(cov.geojson)
            : cov.geojson
          let paths = null
          if (geo.type === 'Polygon') paths = [toLatLng(geo.coordinates[0])]
          else if (geo.type === 'MultiPolygon') paths = geo.coordinates.map(p => toLatLng(p[0]))
          if (!paths) continue
          paths.forEach(p => polygons.push({ paths: p, color }))
          if (!firstCenter && paths[0].length) {
            firstCenter = paths[0][0]
            firstZoom = cov.coverage_type === 'state'
              ? 6
              : cov.coverage_type === 'city'
                ? 10
                : 14
          }
        } catch {}
      }
      this.mapPolygons = polygons
      if (firstCenter) {
        this.mapCenter = firstCenter
        this.mapZoom = firstZoom
      }
    },
  },

  // Recarrega ao navegar entre nós (admin de filho para pai etc.)
  beforeRouteUpdate(to, from, next) {
    next()
    this.$nextTick(() => this.loadAll())
  },
}
</script>

<style scoped>
.node-form-section {
  margin: 4px 0 12px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5e5873;
}

.node-form-section:not(:first-child) {
  margin-top: 12px;
  padding-top: 16px;
  border-top: 1px solid #ebe9f1;
}

.node-logo {
  display: flex;
  align-items: center;
  gap: 16px;
}

.node-logo__sheet {
  position: relative;
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 300px;
  min-height: 110px;
  padding: 14px 20px 16px;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.1);
}

/* 9 cm de uma folha de 21 cm: a mesma proporção do PDF. */
.node-logo__img {
  width: 42.86%;
  height: auto;
  margin-bottom: 4px;
}

.node-logo__empty {
  padding: 14px 0 18px;
  color: #b9b9c3;
  font-size: 0.8rem;
}

.node-logo__line {
  display: block;
  width: 100%;
  height: 5px;
  border-radius: 3px;
  background: #ebe9f1;
}

.node-logo__line--title { width: 55%; height: 7px; background: #d8d6de; }
.node-logo__line--short { width: 70%; }

.node-logo__pending {
  color: #ff9f43;
  font-size: 0.78rem;
  font-weight: 600;
}

.node-logo__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.required {
  color: red;
}

.table th.sortable {
  cursor: pointer;
  user-select: none;
}

.table th.sortable:hover {
  color: #7367f0;
}
</style>
